<template>
  <!-- A pill bottom-right, on every page, while an onboarding runs.
       Same geometry as ClientPackProgress's minimised pill so the two read as
       one behaviour rather than two. -->
  <Teleport to="body">
    <div
      v-if="activeJob"
      class="obp-pill fixed right-4 bottom-20 md:bottom-4 z-[70] flex items-center gap-2.5 py-2 pl-2.5 pr-2 rounded-full border bg-(--nviya-surface-raised) border-(--nviya-border) text-(--nviya-text-primary)"
    >
      <button
        type="button"
        class="flex items-center gap-2.5 pr-1 text-left hover:opacity-80 transition-opacity"
        @click="open"
      >
        <span class="obp-ring w-[18px] h-[18px] rounded-full shrink-0" />
        <span class="min-w-0">
          <span class="block text-xs font-semibold truncate max-w-[13rem]">
            Onboarding {{ activeJob.companyName || 'company' }}
          </span>
          <span class="block text-[11px] truncate max-w-[13rem] text-(--nviya-text-muted)">
            {{ line }}
          </span>
        </span>
        <FontAwesome :icon="['fas', 'chevron-right']" class="w-2.5 h-2.5 text-(--nviya-text-muted)" />
      </button>
      <button
        type="button"
        aria-label="Dismiss"
        title="Dismiss — the onboarding keeps running"
        class="w-6 h-6 flex items-center justify-center rounded-full border-l border-(--nviya-border) text-(--nviya-text-muted) hover:text-(--nviya-text-primary) transition-colors"
        @click="dismiss"
      >
        <FontAwesome :icon="['fas', 'xmark']" class="w-3 h-3" />
      </button>
    </div>
  </Teleport>
</template>

<script setup>
/**
 * The running-onboarding pill. State lives in `useOnboardingJob`; this
 * component only renders it and routes the two clicks.
 *
 * Mounted once in the dashboard layout, so it shows on every page.
 */
import { computed, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import { useOnboardingJob } from '@/composables/useOnboardingJob'

const { activeJob, resume, dismiss } = useOnboardingJob()
const router = useRouter()

// Count only once there is something to count: "0 of 0" on the first poll
// reads as broken.
const line = computed(() => {
  const j = activeJob.value
  if (!j) return ''
  const label = j.label || 'Starting…'
  return j.total ? `${label} · ${j.done} of ${j.total}` : label
})

const open = () => {
  if (activeJob.value) router.push(`/onboarding/${activeJob.value.logId}`)
}

// Picks the user's in-flight run back up after a reload. Safe to call on
// every mount: it returns immediately when a job is already tracked.
onMounted(() => { resume() })
</script>

<style scoped>
/* A floating pill sits above the page, so it keeps a shadow — but the token,
   not a hand-rolled one. Same rule as ClientPackProgress. */
.obp-pill { box-shadow: var(--nviya-shadow-md); }

.obp-ring {
  border: 2.5px solid color-mix(in srgb, var(--nviya-accent) 22%, transparent);
  border-top-color: var(--nviya-accent);
  animation: obp-spin 1s linear infinite;
}
@keyframes obp-spin { to { transform: rotate(360deg); } }
</style>
